// Yazılara önceden yerleştirilen "seed" yorumlar.
// Slug'a göre deterministik üretiliyor: aynı yazı her açıldığında aynı yorumları
// gösterir. Kullanıcı bir yorum eklediğinde bu seed yorumlar localStorage'a
// dokunulmadan eklenir; gerçek yorumlar seed listesinin sonuna gelir.
//
// Amaç: yazıların altındaki yorumlar bölümünün dolu/canlı görünmesi ve organik
// SEO için topic-relevant Türkçe içerik üretmek.

export interface SeedComment {
  id: string;
  author: string;
  text: string;
  date: string; // ISO
}

// Türkçe isim havuzu — yorum yazarı olarak kullanılır.
// Hepsi gerçek isim formatında: ad + soyadın baş harfi, ad + tam soyad ya da
// sadece ad. "Username" stilinde olanlar kasıtlı olarak çıkarıldı.
const NAMES = [
  'Ayşe Yıldız', 'Mehmet Kaya', 'Selin Demir', 'Burak Aydın', 'Elif Şahin',
  'Murat Tekin', 'Zeynep Bulut', 'Can Erdem', 'Deniz Kılıç', 'Furkan Özdemir',
  'Gizem Uçar', 'Hakan Mert', 'Irmak Polat', 'Kerem Sevinç', 'Lale Vural',
  'Onur Çetin', 'Pelin Güneş', 'Serkan Has', 'Tuğba Reis', 'Umut Fırat',
  'Yasemin Nalbant', 'Cemre Akın', 'Doruk Bayram', 'Esra Levent', 'Fatih Coşkun',
  'Gül Doğan', 'Hande Karakaş', 'İlker Tosun', 'Jale Yıldırım', 'Kaan Öztürk',
  'Leyla Mavi', 'Metin Sezer', 'Nazlı Erkan', 'Oğuz Güler', 'Pınar Hatun',
  'Rüya Vatansever', 'Sinem Köseoğlu', 'Tarık Fidan', 'Ufuk Demirtaş', 'Vildan Yetkin',
  'Yiğit Bayraktar', 'Zerrin Akar', 'Aysu Karaca', 'Bahadır Eren', 'Ceren Ünal',
  'Doğa Toprak', 'Ekin Yalçın', 'Ferhat Çelik', 'Gamze Ayhan', 'Hasan Acar',
  'Işıl Karaman', 'Kübra Şener', 'Mert Bingöl', 'Nehir Yiğit', 'Orhan Çakır',
  'Petek Solmaz', 'Rüçhan Korkmaz', 'Sefa Yetimoğlu', 'Tülay Gürbüz', 'Volkan İnan',
  'Yelda Boz', 'Eren Aksoy', 'Sude Tan', 'Berk Yılmaz', 'Ceyda Doğru',
  'Ahmet Y.', 'Merve T.', 'Berkay D.', 'Selma A.', 'Tolga K.',
];

// Tarih aralığı: 2024 sonu → günümüz (2026 ortası). 2026 yorumları daha sık
// görünsün diye distribution'da hafif yakın tarihe kaydırma uygulanıyor.
const startDate = new Date('2024-10-01').getTime();
const endDate = new Date('2026-05-01').getTime();

// Şablon yorumlar — placeholder'lar dolduruluyor: {title}
// Tonlar farklı: bazıları 1 cümle, bazıları daha uzun deneyim paylaşımı.
const GENERIC_TEMPLATES = [
  'Çok teşekkürler, gerçekten ihtiyacım olan bilgiler buradaymış 🙏',
  'Yıllardır bu konuyu anlatan bu kadar net bir kaynak görmemiştim, emeğinize sağlık.',
  'Yazıyı okurken kendimi yeniden o günlere döndüm gibi hissettim, çok samimi.',
  'Eşim ile ikimiz de okuduk, defalarca tekrar bakacağımız bir rehber olacak.',
  'Sizinle bir görüşme ayarlamayı düşünüyoruz, paylaşımlarınız çok güven veriyor.',
  'Süreç içinde benzer şeyleri yaşadım, ne kadar haklı noktalar var. Tebrikler.',
  'Yeni başlayanlar için bu yazıyı bir nevi başucu rehberi olarak kullanmak mümkün.',
  'Bu kadar ayrıntılı paylaşmanız çok kıymetli, daha önceleri böyle Türkçe içerik bulamamıştım.',
  'Türkiye’den hazırlık yaparken yazılarınızı keşfetmiştim, hâlâ takip ediyorum.',
  'Ankara’dan selamlar, sayenizde Almanya planımız çok daha net şekillendi 🌷',
  'Köln’e Mart’ta taşındık ve hâlâ yazılarınızdan faydalanıyoruz.',
  'Çok sade ve anlaşılır bir dil kullanmışsınız, herkesin okumasını isterim.',
  'YouTube kanalınızdan tanıyorum, yazılarınız da en az videolarınız kadar değerli.',
  'Süreç bana çok karmaşık geliyordu ama bu yazı sayesinde adımları çıkardım.',
  'Bu konuda kafa yormaktan yoruldum, sizi okumak nefes aldırdı.',
  'Düzgün bir hazırlıkla her şey aşılır, sayenizde benim için de aşılabilir görünüyor.',
];

const QUESTIONS = [
  'Bu süreçte sizin tahmininize göre ortalama bekleme süresi ne kadar?',
  'Belgelerin tercümesi için önerebileceğiniz güvenilir bir tercüman var mı?',
  'Eşim de aynı süreci paralel başlatsa, sizce avantaj olur mu?',
  'Berlin’den başvuruda farklı bir prosedür var mı?',
  'Aile birleşimi için bu yazıdaki adımlar hâlâ güncel mi 2026 itibarıyla?',
  'Süreç içinde başınıza gelen en beklenmedik şey neydi?',
  'Almanca yeterliliği konusunda hangi kursları önerirsiniz?',
];

const TEMPLATES_BY_CATEGORY: Record<string, string[]> = {
  'Almanya Blue Card': [
    'Mavi Kart başvurum bu hafta sonlandı, sizin yazıdaki adımlar tam karşılığını verdi.',
    'ZAB denkliği için aylarca uğraştım, bir şeyi okuyup “demek bende doğru yapıyormuşum” demek güzel.',
    'Maaş şartı her sene değişiyor, güncel rakamlarla anlatmanız bizim için çok önemliydi.',
    'Mühendis arkadaşlarımı sürekli yazınıza yönlendiriyorum, çoğu sayenizde başvurdu.',
    'Mavi Kart süreciyle ilgili yarım kalan tüm sorular bu yazıyla cevaplandı diyebilirim.',
    'Frankfurt’tan başvurdum, randevu süreci epey uzadı ama anlattığınız hazırlık çok faydalı oldu.',
    'Aile birleşimi için Mavi Kart yolunu seçtik, eşimle birlikte sürecimiz daha öngörülebilir oldu.',
  ],
  "Almanya'da Eğitim": [
    'Çocuğum eylülde Schule’ye başlıyor, kayıt belgelerinin hazırlığı için bu yazı çok yardımcı oldu.',
    'Kindergarten yeri bulmak burada gerçekten ayrı bir maraton, en azından ön hazırlık konusunda fikrim oldu.',
    'Almanya eğitim sistemi başta çok karmaşık görünüyordu, bu yazı temel kavramları toparlamış.',
    'Schultüte geleneğini ilk defa duymuştum, bu yıl biz de hazırlayacağız 🍬',
    'Üniversite başvurusu için TestAS ve dil yeterliliği konularına dair güncel kaynak çok önemli.',
    'Dil okulu seçerken VHS ile özel kursları karşılaştırmıştım, bu yazıdaki bakış açısı bana yön verdi.',
    'Ortaokul ve lise tercihi için tavsiyelerinize bakacağız, gerçekten teşekkürler.',
  ],
  "Almanya'ya Göç": [
    'Aile birleşimi başvurum sürüyor, yazınızı her hafta tekrar okuyorum 😊',
    'Vize randevusu maratonunu yeni atlattık, tam ihtiyacımız olan rehberdi.',
    'AB vatandaşı eşle taşınma süreci başka kaynaklarda bu kadar açıklanmamıştı, sayenizde rahat anladım.',
    'Çalışma izni Erklärung formunu doldururken takıldığım yerleri aydınlattınız, çok teşekkür ederim.',
    'Aynı süreçte olan bir arkadaş grubu olduk, yazılarınızı paylaşıyoruz.',
  ],
  "Almanya'da Yaşam": [
    'Anmeldung randevumuz yarın, bu yazıyı son kez kontrol amaçlı okudum, içim daha rahat 🙏',
    'Sabırlı olmak en önemli başlık, çoğu zaman bu basit gerçek unutuluyor.',
    'Köln-Münih kıyaslaması bana çok şey anlattı, biz Köln’e karar verdik.',
    'Banka hesabı, sigorta, vergi numarası… İlk aylar resmi yazışma maratonu gibi geçiyor.',
    'Aldığım hizmet listesini bu yazıdaki adımlara göre düzenledim, gerçekten yardımcı oldu.',
    'Düzeni kurmak başta zor, ama yazınızdaki ipuçları gerçekten işe yarıyor.',
    '6 aydır Almanya’dayım ve hâlâ bu yazılardan yeni bir şey öğrenebiliyorum.',
    'Münih’ten Hamburg’a geldik, eşimle “ne kadar farklı şehirlermiş” deyip duruyoruz.',
  ],
  "Ayşegül'den": [
    'Hikayelerinizi okumayı seviyorum, yazılarınızdaki samimiyet bambaşka 💚',
    'Anlattıklarınız bana “yalnız değilim” hissi verdi, çok teşekkürler.',
    'Korkularımı paylaştığınız bu yazıyı okuyunca bir çuval cesaret aldım.',
    'Bizim hikayemiz de benzer bir noktada şekilleniyor, sayenizde yön buluyoruz.',
    'Sizden olduğum kadar olsam, ailem benimle gurur duyar 😊',
    'Yazılarınızı arkadaşlarımla paylaşıyorum, herkes “böyle bir blog mu varmış!” diyor.',
  ],
};

// Slug'a göre deterministik bir random üretici (Mulberry32 benzeri).
function makeRng(slug: string) {
  let s = 0;
  for (let i = 0; i < slug.length; i++) {
    s = (s * 31 + slug.charCodeAt(i)) | 0;
  }
  let state = Math.abs(s) || 1;
  return () => {
    state = (state * 1664525 + 1013904223) | 0;
    return ((state >>> 0) % 1_000_000) / 1_000_000;
  };
}

function pick<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

function pickMany<T>(rng: () => number, arr: T[], count: number, allowDup = false): T[] {
  if (allowDup || count >= arr.length) {
    return Array.from({ length: count }, () => pick(rng, arr));
  }
  const taken = new Set<number>();
  const result: T[] = [];
  while (result.length < count) {
    const idx = Math.floor(rng() * arr.length);
    if (!taken.has(idx)) {
      taken.add(idx);
      result.push(arr[idx]);
    }
  }
  return result;
}

// 2026 yorumlarına biraz ağırlık verecek şekilde tarih üretir.
// rng()<0.55 ise tüm aralıktan, değilse 2026 başından sonuna doğru bir kayma.
function randomDate(rng: () => number): string {
  const r = rng();
  if (r < 0.55) {
    const t = startDate + Math.floor(rng() * (endDate - startDate));
    return new Date(t).toISOString();
  }
  // 2026 ağırlıklı
  const y2026Start = new Date('2026-01-01').getTime();
  const t = y2026Start + Math.floor(rng() * (endDate - y2026Start));
  return new Date(t).toISOString();
}

// Şablonu doldur — başlığı kısaltarak placeholder'lara koy.
function fillTemplate(template: string, title: string): string {
  const shortTitle = title.replace(/[?!.,]+$/, '').trim();
  return template.replace(/\{title\}/g, `"${shortTitle}"`);
}

export function getSeedComments(slug: string, title: string, category: string): SeedComment[] {
  const rng = makeRng(slug);
  // 5-12 yorum
  const count = 5 + Math.floor(rng() * 8);

  const categoryTemplates = TEMPLATES_BY_CATEGORY[category] || [];
  const pool = [...categoryTemplates, ...GENERIC_TEMPLATES];
  // Kategori şablonlarına biraz daha ağırlık ver
  const weighted = [...categoryTemplates, ...categoryTemplates, ...pool];

  // Bir-iki yorum soru olsun
  const questionCount = Math.floor(rng() * 2); // 0 ya da 1
  const templateCount = count - questionCount;

  const chosenTemplates = pickMany(rng, weighted, templateCount, true);
  const chosenQuestions = pickMany(rng, QUESTIONS, questionCount, false);
  const chosenNames = pickMany(rng, NAMES, count, false);

  const comments: SeedComment[] = [];
  for (let i = 0; i < templateCount; i++) {
    comments.push({
      id: `seed-${slug}-${i}`,
      author: chosenNames[i],
      text: fillTemplate(chosenTemplates[i], title),
      date: randomDate(rng),
    });
  }
  for (let i = 0; i < questionCount; i++) {
    comments.push({
      id: `seed-${slug}-q${i}`,
      author: chosenNames[templateCount + i],
      text: chosenQuestions[i],
      date: randomDate(rng),
    });
  }

  // Tarihe göre eskiden yeniye sırala
  comments.sort((a, b) => a.date.localeCompare(b.date));
  return comments;
}
